/** leads/LeadDetailDrawer.tsx — 리드 상세 (수정·활동이력·won 전환·FP 포털링크 복사) */
import React, { useCallback, useEffect, useState } from 'react';
import { getLead, updateLead, updateLeadRounds, addActivity, fpPortalLink } from './api';
import LeadAssignModal from './LeadAssignModal';
import type { FpNote } from './types';
import { STATUS_LABEL, stStyle, stLabel } from './types';
import type { Lead, LeadActivity, LeadMeta } from './types';
import { fmtAmount, fmtBizNo, fmtPhone, freshYears } from './calc';

interface Props {
  leadId: string;
  meta: LeadMeta | null;
  onClose: () => void;
  onChanged: () => void;
  onToast: (m: string, t?: string) => void;
}

const KIND_LABEL: Record<string, string> = { call: '📞 통화', visit: '🤝 방문', memo: '📝 메모', status: '상태변경', claim: '선점', assign: '배정', return: '↩ 반납', contact: '☎ 연락처 확보', meeting: '📅 미팅' };

// 버그수정(2026-08-11): meeting 활동은 content가 JSON 문자열({round,text,manage,type})이라
//   그대로 렌더하면 관리자에게 raw JSON이 노출됨. 사람이 읽는 문장으로 정리.
function fmtActContent(a: LeadActivity): string {
  if (a.kind !== 'meeting') return a.content || '';
  try {
    const o = JSON.parse(a.content || '{}') as { round?: number; text?: string; manage?: string; type?: string };
    const head = [o.round ? o.round + '차' : '', o.type ? '[' + o.type + ']' : '', o.manage || ''].filter(Boolean).join(' ');
    return [head, o.text || ''].filter(Boolean).join(' — ');
  } catch { return a.content || ''; }
}

export default function LeadDetailDrawer({ leadId, meta, onClose, onChanged, onToast }: Props) {
  const [lead, setLead] = useState<Lead | null>(null);
  const [acts, setActs] = useState<LeadActivity[]>([]);
  const [fpNotes, setFpNotes] = useState<FpNote[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);
  // 투자 건별(회차) 편집 — §16은 건별로 귀속연도(출자 과세연도부터 2년 내)를 각각 선택 가능 (2026-08-26)
  const [roundEdit, setRoundEdit] = useState(false);
  const [roundRows, setRoundRows] = useState<{ amt: string; date: string }[]>([]);

  const load = useCallback(async () => {
    try {
      const d = await getLead(leadId);
      setLead(d.lead); setActs(d.activities); setFpNotes(d.fp_notes || []);
      setRoundEdit(false);
    } catch (e) { onToast((e as Error).message, 'error'); }
  }, [leadId, onToast]);

  useEffect(() => { load(); }, [load]);

  const saveActivity = async () => {
    if (!memo.trim()) return;
    setBusy(true);
    try {
      await addActivity(leadId, { kind: 'memo', content: memo.trim(), status_to: null });
      setMemo(''); load(); onChanged();
      onToast('기록 완료');
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };

  const copyPortal = async () => {
    if (!lead?.assigned_fp_id) return;
    try {
      const r = await fpPortalLink(lead.assigned_fp_id);
      const url = r.short_url || r.full_url || r.url; // url = 절대 URL (백엔드 toPortalUrls 계약)
      if (navigator.clipboard) { await navigator.clipboard.writeText(url); onToast('FP 리드 포털 링크 복사됨'); }
      else onToast(url);
    } catch (e) { onToast((e as Error).message, 'error'); }
  };

  const saveCorpNo = async (v: string) => {
    try { await updateLead(leadId, { corp_no: v || null }); onToast(v ? '법인등록번호 저장 — FP 포털에 [재무제표]가 열립니다' : '법인등록번호 삭제'); load(); }
    catch (e) { onToast((e as Error).message, 'error'); }
  };

  const savePhone = async (phone: string) => {
    // PUT 응답은 leads.* 단독(fps 조인 없음) — setLead(응답) 시 fp_name 소실로 '미배정' 표시됨 → load()로 재조회
    try { await updateLead(leadId, { phone }); onToast('연락처 저장'); load(); }
    catch (e) { onToast((e as Error).message, 'error'); }
  };

  if (!lead) return null;
  const ys = freshYears(lead.detail?.eligible_years); // 조회 시점 재계산·도과 제외
  const vf = lead.detail?.venture_from ? String(lead.detail.venture_from).slice(0, 10) : null;
  const vt = lead.detail?.venture_to ? String(lead.detail.venture_to).slice(0, 10) : null;

  // 투자 건별(회차) — round 0 = FSC 연 합산 추정(회차 미상)
  const rounds = lead.invest_rounds || [];
  const roundsTotal = rounds.length ? rounds.reduce((s, r) => s + (Number(r.amount) || 0), 0) : (lead.detail?.invest_amount || 0);
  const isAnnualEst = rounds.length <= 1 && (rounds.length === 0 || (rounds[0].no === 0 && String(rounds[0].src || '').includes('자동스캔')));

  const beginRoundEdit = () => {
    const src = rounds.length
      ? rounds.map((r) => ({ amt: String(Math.round((Number(r.amount) || 0) / 10000)), date: r.date ? String(r.date).slice(0, 10) : '' }))
      : [{ amt: String(Math.round((Number(lead.detail?.invest_amount) || 0) / 10000)), date: '' }];
    setRoundRows(src);
    setRoundEdit(true);
  };
  const saveRounds = async () => {
    const parsed = roundRows
      .map((r) => ({ amount: Math.round(Number(String(r.amt).replace(/[^0-9]/g, '')) || 0) * 10000, date: r.date || null }))
      .filter((r) => r.amount > 0);
    if (!parsed.length) { onToast('금액을 1건 이상 입력하세요', 'error'); return; }
    setBusy(true);
    try {
      await updateLeadRounds(leadId, parsed);
      onToast('투자 건별 내역 저장 — 합계 ' + fmtAmount(parsed.reduce((s, r) => s + r.amount, 0)) + ' · ' + parsed.length + '건');
      load(); onChanged();
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };

  return (
    <div className="ld-root">
      <div className="ld-drawer-bg" onClick={onClose}>
      <div className="ld-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="ld-drawer-h">
          <div>
            <span><b style={{ fontSize: 16 }}>{lead.corp_name}</b>
              <span className="ld-badge" style={{ background: stStyle(lead.status).bg, color: stStyle(lead.status).fg, marginLeft: 8 }}>{STATUS_LABEL[lead.status]}</span></span>
            <span className="pm-sub">{lead.address || '주소 미확보'}</span>
          </div>
          <button className="ld-btn" onClick={onClose}>닫기</button>
        </div>
        <div className="ld-drawer-b">
          {/* ① 핵심 요약 */}
          <div className="dr2-grid">
            <div className="dr2-cell"><span className="k">사업자번호</span><span className="v dr-num">{fmtBizNo(lead.biz_no)}</span></div>
            <div className="dr2-cell"><span className="k">대표자</span><span className="v">{lead.ceo_name || '—'}</span></div>
            <div className="dr2-cell hi"><span className="k">투자연도 · 금액{!!lead.detail?.invest_year && isAnnualEst && !roundEdit && <button className="ld-btn" style={{ marginLeft: 8, padding: '1px 8px', fontSize: 11 }} onClick={beginRoundEdit} title="같은 해 복수 증자 등 실제 회차를 등기부로 확인해 건별 입력">회차 수정</button>}</span><span className="v dr-num">{lead.detail?.invest_year || '—'} · {fmtAmount(roundsTotal)}{rounds.length > 1 ? ' · ' + rounds.length + '건' : ''}</span></div>
            <div className="dr2-cell"><span className="k">벤처 유효기간</span><span className="v dr-num">{vf ? vf + ' ~ ' + (vt || '-') : '—'}</span></div>
          </div>

          {/* ①-1 투자 건별(회차) 내역 (2026-08-26): §16은 건별로 귀속연도를 각각 선택 가능 —
               같은 해 2회 증자도 건마다 다른 연도로 공제 신청할 수 있어 반드시 건 단위로 관리 */}
          {/* 연 합산(재무 추정)만 있는 건은 섹션을 숨긴다 — 사용자 지시 2026-09-05 ('이거는 지워도 될 듯'). 회차 수정은 요약 칸의 링크로 진입.
               수동 회차가 있거나 편집 중이면 기존대로 표시 */}
          {!!lead.detail?.invest_year && (roundEdit || !isAnnualEst) && (<>
          <div className="pm-step">투자 내역 (건별)
            <span className="pm-hint">건마다 귀속연도(출자 과세연도부터 2년 내)를 따로 선택해 공제 가능</span>
            {!roundEdit && <button className="ld-btn" style={{ marginLeft: 'auto' }} onClick={beginRoundEdit}>회차 수정</button>}
          </div>
          {!roundEdit && (
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden', marginBottom: 4 }}>
              {(rounds.length ? rounds : [{ id: '_', amount: lead.detail?.invest_amount || 0, no: 0, date: null, src: '' }]).map((r, i) => (
                <div key={r.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', borderTop: i ? '1px solid #F3F4F6' : 'none', fontSize: 13 }}>
                  <span style={{ fontWeight: 700, color: r.no === 0 ? '#B45309' : '#1D4ED8', minWidth: 52 }}>{r.no === 0 ? '연 합산' : r.no + '회차'}</span>
                  <span className="dr-num" style={{ fontWeight: 700 }}>{fmtAmount(r.amount)}</span>
                  {r.date && <span className="dr-num" style={{ color: '#6B7280' }}>{String(r.date).slice(0, 10)} 출자</span>}
                  <span style={{ marginLeft: 'auto', color: '#9CA3AF', fontSize: 11 }}>{String(r.src || '').includes('수동') ? '등기부 확인' : (String(r.src || '').includes('자동스캔') ? '재무 추정' : '')}</span>
                </div>
              ))}
              {isAnnualEst && (
                <div style={{ padding: '6px 12px', background: '#FFFBEB', color: '#92400E', fontSize: 12, borderTop: '1px solid #FDE68A' }}>
                  금융위 연말 자본금 차액 추정(연 합산 · 액면 기준) — 같은 해 복수 증자·주식발행초과금은 반영되지 않습니다. 등기부 확인 후 [회차 수정]으로 건별 분리 입력하세요.
                </div>
              )}
            </div>
          )}
          {roundEdit && (
            <div style={{ border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 12px', marginBottom: 4, background: '#F8FAFF' }}>
              {roundRows.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1D4ED8', minWidth: 40 }}>{i + 1}회차</span>
                  <input className="dr-num" style={{ width: 110, padding: '5px 8px', border: '1px solid #D1D5DB', borderRadius: 6, textAlign: 'right' }} inputMode="numeric"
                    value={r.amt} placeholder="금액"
                    onChange={(e) => setRoundRows(roundRows.map((x, j) => (j === i ? { ...x, amt: e.target.value.replace(/[^0-9]/g, '') } : x)))} />
                  <span style={{ fontSize: 12, color: '#6B7280' }}>만원</span>
                  <input type="date" className="dr-num" style={{ padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 12 }}
                    value={r.date} title="출자일 (선택)"
                    onChange={(e) => setRoundRows(roundRows.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} />
                  {roundRows.length > 1 && <button className="ld-btn" onClick={() => setRoundRows(roundRows.filter((_, j) => j !== i))}>삭제</button>}
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                <button className="ld-btn" onClick={() => setRoundRows([...roundRows, { amt: '', date: '' }])}>+ 건 추가</button>
                <span style={{ fontSize: 12, color: '#4B5563' }}>합계 {fmtAmount(roundRows.reduce((s, r) => s + (Number(r.amt) || 0) * 10000, 0))}</span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button className="ld-btn" onClick={() => setRoundEdit(false)}>취소</button>
                  <button className="ld-btn pri" disabled={busy} onClick={saveRounds}>저장</button>
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 6 }}>실제 회차·금액(주금납입액)은 법인 등기부의 변경등기로 확인하세요. 저장 시 이 연도의 투자금액 합계가 함께 갱신됩니다.</div>
            </div>
          )}
          </>)}

          {/* ② 가능연도 — 파란 박스 (승격 모달과 동일 문법) */}
          <div className={'pm-preview' + (ys.length === 0 ? ' bad' : '')}>
            <b>경정청구 가능연도</b>
            {ys.length === 0 && <span className="ld-year expired">없음 — 시효 도과 또는 기간 불일치</span>}
            {ys.map((y) => <span key={y.year} className={'ld-year' + (y.daysLeft <= 180 ? ' urgent' : '')}>귀속 {y.year} · 마감 {y.deadline} (D-{y.daysLeft})</span>)}
            {rounds.length > 1 && <span className="pm-hint" style={{ display: 'block', marginTop: 4 }}>건별로 위 연도 중 서로 다른 귀속연도를 선택해 각각 신청할 수 있습니다</span>}
          </div>

          {/* ③ 조회·연락 (편집) */}
          <div className="pm-step">조회 · 연락 정보<span className="pm-hint">칸을 벗어나면 자동 저장</span></div>
          <div className="pm-row two">
            <div className="dr-field">
              <label>법인등록번호 <span className="pm-hint">재무제표 조회 키</span></label>
              <input className="dr-num" defaultValue={lead.corp_no || ''} placeholder="13자리 — 등기부·크레탑" inputMode="numeric"
                onBlur={(e) => { const d = e.target.value.replace(/[^0-9]/g, ''); if (d !== (lead.corp_no || '') && (d.length === 13 || d.length === 0)) saveCorpNo(d); }} />
            </div>
            <div className="dr-field">
              <label>연락처</label>
              <input className="dr-num" defaultValue={fmtPhone(lead.phone)} placeholder="확보 시 입력"
                onBlur={(e) => { if (fmtPhone(e.target.value) !== fmtPhone(lead.phone)) savePhone(fmtPhone(e.target.value)); }} />
            </div>
          </div>

          {/* ④ 담당 FP */}
          <div className="pm-step">담당 FP</div>
          <div className="ld-owner">
            <b>{lead.fp_name ? lead.fp_name + (lead.fp_company ? ' · ' + lead.fp_company : '') : (lead.assigned_branch ? '지점 ' + lead.assigned_branch + ' (미선점)' : '미배정')}</b>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className={'ld-btn' + (lead.fp_name || lead.assigned_branch ? '' : ' pri')} onClick={() => setAssignOpen(true)}>
                {lead.fp_name || lead.assigned_branch ? '배정 변경' : 'FP에게 배정'}
              </button>
              {lead.assigned_fp_id && <button className="ld-btn" onClick={copyPortal}>포털링크 복사</button>}
            </div>
          </div>

          {/* ⑤ FP 현장 메모 (읽기 전용) */}
          {fpNotes.length > 0 && (
            <div className="ld-fpmemo">
              <div className="ld-fpmemo-t">FP 현장 메모 {fpNotes.length}개</div>
              {fpNotes.map((n) => (
                <div key={n.id} className="ld-fpmemo-item">
                  <div className="ld-fpmemo-body">{n.content}</div>
                  <div className="ld-fpmemo-meta">{n.author_name} · {new Date(n.created_at).toLocaleString('ko-KR')}</div>
                </div>
              ))}
            </div>
          )}

          {/* ⑥ 관리자 메모 */}
          <div className="pm-step">관리자 메모<span className="pm-hint">상태는 FP 보고·배정·반납으로 자동 변경</span></div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <textarea className="dr-memo" rows={1} value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="예: 대표님과 통화 — 다음주 재확인" />
            <button className="ld-btn pri" disabled={busy || !memo.trim()} onClick={saveActivity}>기록</button>
          </div>

          {/* ⑦ 활동 이력 */}
          <div className="pm-step">활동 이력<span className="pm-hint">{acts.length}건</span></div>
          {acts.map((a) => (
            <div key={a.id} className="ld-act">
              <span className="when">{new Date(a.created_at).toLocaleString('ko-KR')}</span>
              <span className="who">{a.author_type === 'fp' ? 'FP ' : ''}{a.author_name}</span>
              <span style={{ color: '#4B5563' }}>{KIND_LABEL[a.kind] || a.kind}</span>
              {a.status_to && <span className="ld-badge" style={{ background: stStyle(a.status_to).bg, color: stStyle(a.status_to).fg, marginLeft: 6 }}>{stLabel(a.status_to)}</span>}
              {a.content && <div className="body">{fmtActContent(a)}</div>}
            </div>
          ))}
          {acts.length === 0 && <div style={{ color: '#9CA3AF', fontSize: 12, padding: '6px 0 2px' }}>기록이 없습니다</div>}
        </div>
      </div>
      </div>
      {assignOpen && <LeadAssignModal leadIds={[leadId]} meta={meta} onClose={() => setAssignOpen(false)}
        onDone={() => { setAssignOpen(false); load(); onChanged(); }} onToast={onToast} />}
    </div>
  );
}
